/**
 * DailyTasksPage — Personal & team daily recurring tasks
 * • Private tasks: visible only to the owner
 * • Team tasks: shared with chosen members, each tracks their own completion
 * • Missed tasks auto-carry to today with a "carried over" badge
 * • History shows last 30 days of completions
 * • Assigned tab shows tasks from the main tasks collection
 * Spacemancraft Pvt Ltd
 */
import { useState, useEffect, useRef, createContext, useContext, useMemo } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, orderBy, serverTimestamp,
  arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { createNotification, extractMentions } from '../notifUtils';
import { useTheme } from '../appContext';
import TaskCompleteModal from '../components/TaskCompleteModal/TaskCompleteModal';
import { actorStamp } from '../utils/actorStamp';

const db = () => getFirestore(getApp());

// ── Date helpers ──────────────────────────────────────────────────────────────
// IST-based, not UTC: `.toISOString()` reports the UTC calendar date, which is
// still "yesterday" for ~5.5 hours every night in India (UTC+5:30) — that
// mismatch made completions written just after local midnight fail to match
// `todayStr()` on next load, showing checked tasks as unchecked again.
const todayStr   = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const offsetStr  = (n) => { const d = new Date(Date.now() + n * 86400000); return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); };
const fmtDate    = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' });
const fmtDateLong= (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

function getMissedCount(task, uid) {
  if (!task.startDate || task.startDate >= todayStr()) return 0;
  const done = new Set(task.completions?.[uid] || []);
  let count = 0;
  let d = new Date(task.startDate + 'T12:00:00');
  const yest = new Date(offsetStr(-1) + 'T12:00:00');
  while (d <= yest) {
    if (!done.has(d.toISOString().slice(0, 10))) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

function isDoneToday(task, uid) {
  return (task.completions?.[uid] || []).includes(todayStr());
}

function getPastCompletions(tasks, uid) {
  const today = todayStr();
  const entries = [];
  for (const task of tasks) {
    for (const date of (task.completions?.[uid] || [])) {
      if (date >= today) continue;
      const log = task.completionLogs?.[uid]?.[date] || {
        completedAt: `${date}T12:00:00.000Z`,
        thread: [],
      };
      entries.push({ task, date, log });
    }
  }
  return entries.sort((a, b) => b.date.localeCompare(a.date));
}

/** All completion entries including today — for the Completed tab. */
function getCompletedEntries(tasks, uid) {
  const entries = [];
  for (const task of tasks) {
    for (const date of (task.completions?.[uid] || [])) {
      const log = task.completionLogs?.[uid]?.[date] || {
        completedAt: `${date}T12:00:00.000Z`,
        thread: [],
      };
      entries.push({ task, date, log });
    }
  }
  return entries.sort((a, b) => b.date.localeCompare(a.date));
}

function isScheduledForToday(task, today = todayStr()) {
  return !task.startDate || task.startDate <= today;
}

const MAX_ATTACH_MB = 25;
const ATTACH_ACCEPT = 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip';

async function uploadTaskThreadFile(taskId, date, file) {
  return uploadTaskAttachment(taskId, `completed/${date}`, file);
}

/** Uploads one file under daily_tasks/{taskId}/{folder}/ and returns its attachment record. */
async function uploadTaskAttachment(taskId, folder, file) {
  if (file.size > MAX_ATTACH_MB * 1024 * 1024) {
    throw new Error(`${file.name} is larger than ${MAX_ATTACH_MB} MB`);
  }
  const storage = getStorage(getApp());
  const filename = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const path = `daily_tasks/${taskId}/${folder}/${filename}`;
  const storageRef = ref(storage, path);
  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed', () => {}, reject, resolve);
  });
  const url = await getDownloadURL(storageRef);
  return {
    url,
    path,
    type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file',
    name: file.name,
  };
}

// ── Theme (synced with app dark / light) ─────────────────────────────────────
function buildDailyTasksColors(theme) {
  return {
    bg: theme.bg,
    panel: theme.cardBg || theme.panel,
    panel2: theme.eqBg || theme.bg,
    border: theme.border,
    text: theme.text,
    bright: theme.bright || theme.text,
    dim: theme.muted,
    muted: theme.muted,
    accent: theme.accent,
    green: '#1D9E75',
    red: '#D85A30',
    yellow: '#BA7517',
    purple: '#7F77DD',
    mono: theme.monoFont || "'Share Tech Mono', monospace",
    sans: theme.font || "'Inter', system-ui, sans-serif",
    dark: theme.dark,
    hoverShadow: theme.dark ? '0 2px 12px rgba(0,0,0,0.28)' : '0 2px 12px rgba(15,23,42,0.08)',
    overlay: theme.dark ? 'rgba(0,0,0,0.62)' : 'rgba(15,23,42,0.45)',
  };
}

const DailyTasksThemeContext = createContext(null);
function useDT() {
  const ctx = useContext(DailyTasksThemeContext);
  if (!ctx) throw new Error('DailyTasks components must render inside DailyTasksPage');
  return ctx;
}

const ROLE_COLOR = {
  admin:'#ef4444', mp:'#6366f1', embedded:'#f59e0b',
  system:'#10b981', ops:'#e11d48', mentor:'#0ea5e9', guest:'#94a3b8',
};

// ── Small reusable bits ───────────────────────────────────────────────────────
function Avatar({ name, size = 28, color }) {
  const T = useDT();
  const accent = color || T.accent;
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', flexShrink:0,
      background:`${accent}18`, border:`1px solid ${accent}44`,
      display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:T.mono, fontSize:size * 0.38, color:accent, fontWeight:700 }}>
      {(name||'?').slice(0,2).toUpperCase()}
    </div>
  );
}

function Pill({ label, color, active, onClick }) {
  const T = useDT();
  const accent = color || T.accent;
  return (
    <button type="button" onClick={onClick}
      style={{ padding:'4px 10px', fontFamily:T.mono, fontSize:9, cursor:'pointer', borderRadius:10,
        background: active ? `${accent}20` : 'transparent',
        border:`1px solid ${active ? accent : T.border}`,
        color: active ? accent : T.muted }}>
      {label}
    </button>
  );
}

/** Thumbnails for images, chips for other files. `onRemove(index)` shows a ✕ per item. */
function AttachmentStrip({ items, onRemove }) {
  const T = useDT();
  if (!items?.length) return null;
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
      {items.map((a, i) => (
        <span key={a.path || a.url || i} style={{ position:'relative', display:'inline-flex' }}>
          <a href={a.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            title={a.name || 'Attachment'} style={{ textDecoration:'none' }}>
            {a.type === 'image' ? (
              <img src={a.url} alt={a.name || ''} style={{ width:64, height:48, objectFit:'cover',
                borderRadius:4, border:`1px solid ${T.border}`, display:'block' }} />
            ) : (
              <span style={{ display:'inline-block', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis',
                whiteSpace:'nowrap', fontFamily:T.mono, fontSize:9, color:T.muted, padding:'4px 8px',
                border:`1px solid ${T.border}`, borderRadius:4, background:T.panel }}>
                {a.type === 'video' ? '🎬' : '📎'} {a.name || 'File'}
              </span>
            )}
          </a>
          {onRemove && (
            <button type="button" aria-label={`Remove ${a.name || 'attachment'}`}
              onClick={e => { e.stopPropagation(); onRemove(i); }}
              style={{ position:'absolute', top:-6, right:-6, width:16, height:16, borderRadius:'50%',
                border:`1px solid ${T.border}`, background:T.panel, color:T.dim, fontSize:9,
                lineHeight:1, cursor:'pointer', padding:0 }}>✕</button>
          )}
        </span>
      ))}
    </div>
  );
}

/** Files picked but not uploaded yet. */
function PendingFiles({ files, onRemove }) {
  const T = useDT();
  if (!files.length) return null;
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, margin:'6px 0' }}>
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} style={{ fontFamily:T.mono, fontSize:9, color:T.muted,
          padding:'3px 8px', border:`1px dashed ${T.border}`, borderRadius:4 }}>
          📎 {f.name}
          <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${f.name}`}
            style={{ marginLeft:6, background:'none', border:'none', color:T.dim, cursor:'pointer', fontSize:9 }}>✕</button>
        </span>
      ))}
    </div>
  );
}

function AttachButton({ onPick, label = '📎', title = 'Attach files', disabled }) {
  const T = useDT();
  const input = useRef(null);
  return (
    <>
      <input ref={input} type="file" accept={ATTACH_ACCEPT} multiple hidden
        onChange={e => { onPick(Array.from(e.target.files || [])); e.target.value = ''; }} />
      <button type="button" title={title} aria-label={title} disabled={disabled}
        onClick={e => { e.stopPropagation(); input.current?.click(); }}
        style={{ padding:'6px 10px', fontFamily:T.mono, fontSize:9, background:'transparent',
          border:`1px solid ${T.border}`, color:T.dim, cursor: disabled ? 'wait' : 'pointer',
          borderRadius:6, whiteSpace:'nowrap' }}>{label}</button>
    </>
  );
}

// ── Add Task Forms ────────────────────────────────────────────────────────────
function AddPrivateTaskForm({ userId, onClose }) {
  const T = useDT();
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [dueDate,     setDueDate]     = useState('');
  const [saving,      setSaving]      = useState(false);
  const inp = useRef(null);
  useEffect(() => inp.current?.focus(), []);

  async function save(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    await addDoc(collection(db(), 'daily_tasks'), {
      title: title.trim(),
      description: description.trim(),
      dueDate: dueDate || null,
      type: 'private',
      ownerId: userId,
      teamMembers: [],
      completions: {},
      comments: [],
      startDate: todayStr(),
      active: true,
      createdAt: serverTimestamp(),
    });
    setSaving(false);
    onClose();
  }

  return (
    <form onSubmit={save} style={{ marginBottom:8, padding:'14px 16px',
      background:T.panel2, border:`1px solid ${T.purple}33`, borderRadius:8 }}>
      <input ref={inp} value={title} onChange={e => setTitle(e.target.value)}
        placeholder="New private task…"
        style={{ width:'100%', background:'transparent', border:'none', outline:'none',
          color:T.bright, fontFamily:T.sans, fontSize:14, marginBottom:10,
          borderBottom:`1px solid ${T.border}`, padding:'0 0 8px' }} />
      <textarea value={description} onChange={e => setDescription(e.target.value)}
        placeholder="Description (optional)…"
        rows={2}
        style={{ width:'100%', background:'transparent', border:`1px solid ${T.border}`,
          borderRadius:6, outline:'none', resize:'vertical',
          color:T.text, fontFamily:T.sans, fontSize:13, padding:'7px 10px',
          marginBottom:10, boxSizing:'border-box' }} />
      <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, flex:1, minWidth:140 }}>
          <span style={{ fontFamily:T.mono, fontSize:9, color:T.dim, whiteSpace:'nowrap' }}>DUE DATE</span>
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
            min={todayStr()}
            style={{ flex:1, background:T.panel, border:`1px solid ${T.border}`,
              borderRadius:6, outline:'none', color:T.text, fontFamily:T.sans,
              fontSize:12, padding:'5px 8px' }} />
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button type="submit" disabled={saving || !title.trim()}
            style={{ padding:'6px 16px', fontFamily:T.mono, fontSize:9,
              background:`${T.purple}18`, border:`1px solid ${T.purple}`,
              color:T.purple, cursor:saving?'wait':'pointer', borderRadius:4 }}>
            {saving ? '…' : 'ADD'}
          </button>
          <button type="button" onClick={onClose}
            style={{ background:'none', border:`1px solid ${T.border}`, color:T.dim,
              cursor:'pointer', fontSize:12, padding:'6px 12px', borderRadius:4,
              fontFamily:T.mono }}>CANCEL</button>
        </div>
      </div>
    </form>
  );
}

function AddTeamTaskForm({ userId, userName, members, onClose }) {
  const T = useDT();
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [dueDate,     setDueDate]     = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving,      setSaving]      = useState(false);
  const inp = useRef(null);
  useEffect(() => inp.current?.focus(), []);

  function toggle(uid) {
    setSelectedIds(p => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);
  }

  async function save(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    const teamMembers = [userId, ...selectedIds.filter(id => id !== userId)];
    const memberNames = {};
    memberNames[userId] = userName;
    members.filter(m => selectedIds.includes(m.uid)).forEach(m => { memberNames[m.uid] = m.name || m.email; });
    await addDoc(collection(db(), 'daily_tasks'), {
      title: title.trim(),
      description: description.trim(),
      dueDate: dueDate || null,
      type: 'team',
      ownerId: userId,
      teamMembers,
      memberNames,
      completions: {},
      comments: [],
      startDate: todayStr(),
      active: true,
      createdAt: serverTimestamp(),
    });
    setSaving(false);
    onClose();
  }

  return (
    <form onSubmit={save} style={{ padding:'14px 16px', background:T.panel2,
      border:`1px solid ${T.accent}33`, borderRadius:8, marginBottom:8 }}>
      <input ref={inp} value={title} onChange={e => setTitle(e.target.value)}
        placeholder="New team task…"
        style={{ width:'100%', background:'transparent', border:'none', outline:'none',
          color:T.bright, fontFamily:T.sans, fontSize:14, marginBottom:10,
          borderBottom:`1px solid ${T.border}`, padding:'0 0 8px' }} />
      <textarea value={description} onChange={e => setDescription(e.target.value)}
        placeholder="Description (optional)…"
        rows={2}
        style={{ width:'100%', background:'transparent', border:`1px solid ${T.border}`,
          borderRadius:6, outline:'none', resize:'vertical',
          color:T.text, fontFamily:T.sans, fontSize:13, padding:'7px 10px',
          marginBottom:10, boxSizing:'border-box' }} />
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <span style={{ fontFamily:T.mono, fontSize:9, color:T.dim, whiteSpace:'nowrap' }}>DUE DATE</span>
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
          min={todayStr()}
          style={{ background:T.panel, border:`1px solid ${T.border}`,
            borderRadius:6, outline:'none', color:T.text, fontFamily:T.sans,
            fontSize:12, padding:'5px 8px' }} />
      </div>
      <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, letterSpacing:1, marginBottom:8 }}>
        SELECT TEAM MEMBERS
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
        {members.filter(m => m.uid !== userId).map(m => (
          <Pill key={m.uid} label={m.name || m.email}
            color={ROLE_COLOR[m.role] || T.accent}
            active={selectedIds.includes(m.uid)}
            onClick={() => toggle(m.uid)} />
        ))}
        {members.filter(m => m.uid !== userId).length === 0 && (
          <span style={{ fontFamily:T.mono, fontSize:10, color:T.dim }}>No other team members found.</span>
        )}
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button type="submit" disabled={saving || !title.trim()}
          style={{ padding:'7px 18px', fontFamily:T.mono, fontSize:9,
            background:`${T.accent}18`, border:`1px solid ${T.accent}`,
            color:T.accent, cursor:saving?'wait':'pointer', borderRadius:4 }}>
          {saving ? 'SAVING…' : 'CREATE TEAM TASK'}
        </button>
        <button type="button" onClick={onClose}
          style={{ padding:'7px 14px', background:'transparent', border:`1px solid ${T.border}`,
            color:T.dim, fontFamily:T.mono, fontSize:9, cursor:'pointer', borderRadius:4 }}>
          CANCEL
        </button>
      </div>
    </form>
  );
}

// ── Task Detail Modal ─────────────────────────────────────────────────────────
function TaskDetailModal({ task, userId, userName, members, onToggle, onDelete, isOwner, onClose }) {
  const T = useDT();
  const done   = isDoneToday(task, userId);
  const missed = getMissedCount(task, userId);
  const isTeam = task.type === 'team';
  const [newSub,     setNewSub]     = useState('');
  const [savingSub,  setSavingSub]  = useState(false);
  const [newComment, setNewComment] = useState('');
  const [savingCmt,  setSavingCmt]  = useState(false);
  const [completionText, setCompletionText] = useState('');
  const [completionFiles, setCompletionFiles] = useState([]);
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [newSubAssignee, setNewSubAssignee] = useState('');
  const [newSubFiles,    setNewSubFiles]    = useState([]);
  const [uploadingSub,   setUploadingSub]   = useState(null);
  const [cmtFiles,   setCmtFiles]   = useState([]);
  const [replyTo,    setReplyTo]    = useState(null);
  const [actionErr,  setActionErr]  = useState('');
  const commentRef = useRef(null);
  const completionFileRef = useRef(null);
  const todayDate = todayStr();
  const todayCompletion = task.completionLogs?.[userId]?.[todayDate];
  const taskRef = doc(db(), 'daily_tasks', task.id);

  const memberById = useMemo(() => Object.fromEntries((members || []).map(m => [m.uid, m])), [members]);
  const nameOf = uid => (uid === userId ? (userName || 'Me')
    : task.memberNames?.[uid] || memberById[uid]?.name || memberById[uid]?.email || 'Member');
  const inTask = uid => uid === task.ownerId || (task.teamMembers || []).includes(uid);
  // Assignee picker: people already on the task first, then everyone else.
  const taskPeople  = [...new Set([task.ownerId, ...(task.teamMembers || [])])].filter(u => u && u !== userId);
  const otherPeople = (members || []).filter(m => !inTask(m.uid));

  function fail(ex) {
    console.warn('Task update failed:', ex);
    setActionErr(ex?.code === 'permission-denied' ? 'You do not have permission to change this task.' : (ex?.message || 'Something went wrong.'));
    setTimeout(() => setActionErr(''), 5000);
  }

  /** Fields that add `uid` to the task (a private task becomes a team task) so they can see it. */
  function membershipPatch(uid) {
    if (!uid || inTask(uid)) return {};
    const owner = task.ownerId;
    const patch = {
      type: 'team',
      teamMembers: [...new Set([owner, ...(task.teamMembers || []), uid])],
      [`memberNames.${uid}`]: nameOf(uid),
    };
    if (!task.memberNames?.[owner]) patch[`memberNames.${owner}`] = nameOf(owner);
    return patch;
  }

  async function notifyAssignee(uid, title) {
    if (!uid || uid === userId) return;
    await createNotification({
      recipientId: uid,
      type:     'TASK_ASSIGNED',
      title:    `📌 ${userName || 'Someone'} assigned you a subtask`,
      body:     `"${title}" in task "${task.title}"`,
      fromId:   userId,
      fromName: userName || 'Me',
      link:     'tasks',
    });
  }

  async function uploadAll(folder, files) {
    const out = [];
    for (const f of files) {
      const a = await uploadTaskAttachment(task.id, folder, f);
      out.push({ ...a, byUid: userId, byName: userName || 'Me', at: new Date().toISOString() });
    }
    return out;
  }

  async function addSubtask(e) {
    e.preventDefault();
    if (!newSub.trim() || savingSub) return;
    setSavingSub(true);
    try {
      const id = Date.now().toString();
      const assigneeId = newSubAssignee === 'me' ? userId : newSubAssignee || null;
      const subtask = {
        id, title: newSub.trim(), completed: false, createdAt: new Date().toISOString(),
        createdBy: userId,
        assigneeId,
        assigneeName: assigneeId ? nameOf(assigneeId) : null,
        attachments: await uploadAll(`subtasks/${id}`, newSubFiles),
      };
      await updateDoc(taskRef, {
        subtasks: [...(task.subtasks || []), subtask],
        ...membershipPatch(assigneeId),
      });
      await notifyAssignee(assigneeId, subtask.title);
      setNewSub('');
      setNewSubAssignee('');
      setNewSubFiles([]);
    } catch (ex) { fail(ex); }
    setSavingSub(false);
  }

  function patchSubtask(subId, change, extra = {}) {
    return updateDoc(taskRef, {
      subtasks: (task.subtasks || []).map(s => (s.id === subId ? { ...s, ...change(s) } : s)),
      ...extra,
    });
  }

  async function toggleSubtask(sub) {
    try {
      await patchSubtask(sub.id, s => ({
        completed: !s.completed,
        completedBy: !s.completed ? userId : null,
        completedAt: !s.completed ? new Date().toISOString() : null,
      }));
    } catch (ex) { fail(ex); }
  }

  async function assignSubtask(sub, value) {
    const assigneeId = value === 'me' ? userId : value || null;
    if (assigneeId === (sub.assigneeId || null)) return;
    try {
      await patchSubtask(sub.id, () => ({ assigneeId, assigneeName: assigneeId ? nameOf(assigneeId) : null }),
        membershipPatch(assigneeId));
      await notifyAssignee(assigneeId, sub.title);
    } catch (ex) { fail(ex); }
  }

  async function attachToSubtask(sub, files) {
    if (!files.length) return;
    setUploadingSub(sub.id);
    try {
      const added = await uploadAll(`subtasks/${sub.id}`, files);
      await patchSubtask(sub.id, s => ({ attachments: [...(s.attachments || []), ...added] }));
    } catch (ex) { fail(ex); }
    setUploadingSub(null);
  }

  async function removeSubAttachment(sub, index) {
    try {
      await patchSubtask(sub.id, s => ({ attachments: (s.attachments || []).filter((_, i) => i !== index) }));
    } catch (ex) { fail(ex); }
  }

  async function deleteSubtask(sub) {
    try {
      await updateDoc(taskRef, { subtasks: (task.subtasks || []).filter(s => s.id !== sub.id) });
    } catch (ex) { fail(ex); }
  }

  async function addComment(e) {
    e.preventDefault();
    if ((!newComment.trim() && cmtFiles.length === 0) || savingCmt) return;
    setSavingCmt(true);
    try {
      const id = Date.now().toString();
      const comment = {
        id,
        text: newComment.trim(),
        authorId: userId,
        authorName: userName || 'Me',
        time: new Date().toISOString(),
        // Replies stay one level deep: replying to a reply threads under its parent.
        parentId: replyTo ? (replyTo.parentId && commentIds.has(replyTo.parentId) ? replyTo.parentId : replyTo.id) : null,
        attachments: await uploadAll(`comments/${id}`, cmtFiles),
      };
      await updateDoc(taskRef, { comments: arrayUnion(comment) });

      const preview = comment.text.slice(0, 80) || `${comment.attachments.length} attachment(s)`;
      const notified = new Set([userId]);
      if (replyTo && !notified.has(replyTo.authorId)) {
        notified.add(replyTo.authorId);
        await createNotification({
          recipientId: replyTo.authorId,
          type:     'REPORT_COMMENT',
          title:    `💬 ${userName || 'Someone'} replied to your comment`,
          body:     `In task "${task.title}": ${preview}`,
          fromId:   userId,
          fromName: userName || 'Me',
          link:     'tasks',
        });
      }
      // Notify @mentioned members
      for (const m of extractMentions(newComment, members || [])) {
        if (notified.has(m.uid)) continue;
        notified.add(m.uid);
        await createNotification({
          recipientId: m.uid,
          type:     'mention',
          title:    `🚀 ${userName || 'Someone'} mentioned you`,
          body:     `In task "${task.title}": ${preview}`,
          fromId:   userId,
          fromName: userName || 'Me',
          link:     'tasks',
        });
      }
      setNewComment('');
      setCmtFiles([]);
      setReplyTo(null);
    } catch (ex) { fail(ex); }
    setSavingCmt(false);
  }

  async function deleteComment(comment) {
    // Deleting a comment also removes its replies.
    const updated = (task.comments || []).filter(c => c.id !== comment.id && c.parentId !== comment.id);
    try { await updateDoc(taskRef, { comments: updated }); } catch (ex) { fail(ex); }
  }

  function startReply(c) {
    setReplyTo(c);
    setTimeout(() => commentRef.current?.focus(), 0);
  }

  const allComments = (task.comments || []).slice().sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  const commentIds  = new Set(allComments.map(c => c.id));
  const topComments = allComments.filter(c => !c.parentId || !commentIds.has(c.parentId));
  const repliesOf   = id => allComments.filter(c => c.parentId === id);

  async function addCompletionThread(e) {
    e.preventDefault();
    if ((!completionText.trim() && completionFiles.length === 0) || savingCompletion || !done) return;
    setSavingCompletion(true);
    try {
      let attachments = [];
      for (const file of completionFiles) {
        attachments.push(await uploadTaskThreadFile(task.id, todayDate, file));
      }
      const message = {
        id: Date.now().toString(),
        text: completionText.trim(),
        authorId: userId,
        authorName: userName || 'Me',
        time: new Date().toISOString(),
        attachments,
      };
      const logs = { ...(task.completionLogs || {}) };
      const userLogs = { ...(logs[userId] || {}) };
      const existing = userLogs[todayDate] || { completedAt: new Date().toISOString(), thread: [] };
      userLogs[todayDate] = { ...existing, thread: [...(existing.thread || []), message] };
      logs[userId] = userLogs;
      await updateDoc(doc(db(), 'daily_tasks', task.id), { completionLogs: logs });

      const mentioned = extractMentions(completionText, members || []);
      for (const m of mentioned) {
        await createNotification({
          recipientId: m.uid,
          type: 'mention',
          title: `🚀 ${userName || 'Someone'} mentioned you`,
          body: `On completed task "${task.title}": ${completionText.trim().slice(0, 80)}`,
          fromId: userId,
          fromName: userName || 'Me',
          link: 'tasks',
        });
      }
      setCompletionText('');
      setCompletionFiles([]);
    } catch (ex) {
      console.warn('Completion thread post failed:', ex);
    }
    setSavingCompletion(false);
  }

  return (
    <div style={{ position:'fixed', inset:0, background:T.overlay, zIndex:500,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'20px 16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:14,
        width:'100%', maxWidth:560, maxHeight:'85vh', overflowY:'auto',
        borderLeft:`4px solid ${done ? T.green : missed > 0 ? T.yellow : isTeam ? T.accent : T.purple}` }}>

        {actionErr && (
          <div role="alert" style={{ position:'sticky', top:0, zIndex:2, padding:'8px 20px', fontFamily:T.mono,
            fontSize:10, color:T.red, background:T.panel, borderBottom:`1px solid ${T.red}44` }}>{actionErr}</div>
        )}

        {/* Header */}
        <div style={{ padding:'18px 20px', borderBottom:`1px solid ${T.border}`,
          display:'flex', alignItems:'flex-start', gap:12 }}>
          <button type="button" onClick={() => onToggle(task)}
            style={{ width:22, height:22, borderRadius:5, flexShrink:0, marginTop:2,
              background: done ? T.green : 'transparent',
              border:`2px solid ${done ? T.green : T.border}`,
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {done && <span style={{ color:'#fff', fontSize:12 }}>✓</span>}
          </button>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:16, fontWeight:700, color: done ? T.green : T.bright, marginBottom:6 }}>
              {task.title}
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {isTeam
                ? <span style={{ fontFamily:T.mono, fontSize:8, color:T.accent,
                    background:`${T.accent}15`, border:`1px solid ${T.accent}33`,
                    padding:'1px 7px', borderRadius:8 }}>TEAM</span>
                : <span style={{ fontFamily:T.mono, fontSize:8, color:T.purple,
                    background:`${T.purple}15`, border:`1px solid ${T.purple}33`,
                    padding:'1px 7px', borderRadius:8 }}>🔒 PRIVATE</span>
              }
              {missed > 0 && !done && (
                <span style={{ fontFamily:T.mono, fontSize:8, color:T.yellow,
                  background:`${T.yellow}15`, border:`1px solid ${T.yellow}33`,
                  padding:'1px 7px', borderRadius:8 }}>
                  📌 {missed} day{missed > 1 ? 's' : ''} overdue
                </span>
              )}
              {done && (
                <span style={{ fontFamily:T.mono, fontSize:8, color:T.green,
                  background:`${T.green}15`, border:`1px solid ${T.green}33`,
                  padding:'1px 7px', borderRadius:8 }}>✓ DONE TODAY</span>
              )}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', color:T.dim, cursor:'pointer', fontSize:20, flexShrink:0 }}>×</button>
        </div>

        {/* Description */}
        {task.description && (
          <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, letterSpacing:2, marginBottom:6 }}>DESCRIPTION</div>
            <div style={{ fontSize:13, color:T.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{task.description}</div>
          </div>
        )}

        {/* Due date */}
        {task.dueDate && (
          <div style={{ padding:'10px 20px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, letterSpacing:2, marginBottom:4 }}>DUE DATE</div>
            <div style={{ fontFamily:T.mono, fontSize:10,
              color: task.dueDate < todayStr() ? T.red : task.dueDate === todayStr() ? T.yellow : T.muted }}>
              {fmtDate(task.dueDate)}
              {task.dueDate < todayStr() && <span style={{ marginLeft:6, color:T.red }}>OVERDUE</span>}
              {task.dueDate === todayStr() && <span style={{ marginLeft:6, color:T.yellow }}>DUE TODAY</span>}
            </div>
          </div>
        )}

        {/* Team members */}
        {isTeam && (
          <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, letterSpacing:2, marginBottom:10 }}>TEAM MEMBERS</div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              {(task.teamMembers || []).map(uid => {
                const name = task.memberNames?.[uid] || uid.slice(0, 8);
                const memberDone = isDoneToday(task, uid);
                return (
                  <div key={uid} style={{ display:'flex', alignItems:'center', gap:6,
                    padding:'5px 10px', borderRadius:8,
                    background: memberDone ? `${T.green}12` : T.panel2,
                    border:`1px solid ${memberDone ? T.green + '44' : T.border}` }}>
                    <div style={{ width:18, height:18, borderRadius:'50%',
                      background: memberDone ? `${T.green}20` : `${T.border}44`,
                      border:`1px solid ${memberDone ? T.green : T.border}`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:8,
                      fontFamily:T.mono, color: memberDone ? T.green : T.dim }}>
                      {memberDone ? '✓' : (name[0]||'?').toUpperCase()}
                    </div>
                    <span style={{ fontFamily:T.mono, fontSize:9,
                      color: memberDone ? T.green : T.muted }}>{name.split(' ')[0]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Subtasks */}
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
          <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, letterSpacing:2, marginBottom:10 }}>
            SUBTASKS ({(task.subtasks || []).length})
          </div>
          {(task.subtasks || []).map(sub => {
            const mine = sub.assigneeId === userId;
            return (
              <div key={sub.id} style={{ marginBottom:6, padding:'7px 10px', borderRadius:6,
                background: sub.completed ? `${T.green}08` : mine ? `${T.accent}0d` : T.panel2,
                border:`1px solid ${sub.completed ? T.green + '22' : mine ? T.accent + '44' : T.border}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <button type="button" onClick={() => toggleSubtask(sub)}
                    aria-label={sub.completed ? 'Mark subtask not done' : 'Mark subtask done'}
                    style={{ width:16, height:16, borderRadius:3, flexShrink:0,
                      background: sub.completed ? T.green : 'transparent',
                      border:`2px solid ${sub.completed ? T.green : T.border}`,
                      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {sub.completed && <span style={{ color:'#fff', fontSize:9, lineHeight:1 }}>✓</span>}
                  </button>
                  <span style={{ flex:'1 1 140px', fontSize:12, color: sub.completed ? T.dim : T.text,
                    textDecoration: sub.completed ? 'line-through' : 'none' }}>{sub.title}</span>
                  <select value={sub.assigneeId === userId ? 'me' : sub.assigneeId || ''}
                    onChange={e => assignSubtask(sub, e.target.value)}
                    aria-label={`Assignee for ${sub.title}`}
                    style={{ maxWidth:150, background:T.panel, border:`1px solid ${sub.assigneeId ? T.accent + '66' : T.border}`,
                      color: sub.assigneeId ? T.accent : T.dim, fontFamily:T.mono, fontSize:9,
                      padding:'3px 6px', borderRadius:10, outline:'none', cursor:'pointer' }}>
                    <option value="">👤 Unassigned</option>
                    <option value="me">👤 Me</option>
                    {taskPeople.map(u => <option key={u} value={u}>👤 {nameOf(u)}</option>)}
                    {sub.assigneeId && sub.assigneeId !== userId && !inTask(sub.assigneeId) && (
                      <option value={sub.assigneeId}>👤 {sub.assigneeName || nameOf(sub.assigneeId)}</option>
                    )}
                    {otherPeople.length > 0 && (
                      <optgroup label="Add to this task">
                        {otherPeople.map(m => <option key={m.uid} value={m.uid}>{m.name || m.email}</option>)}
                      </optgroup>
                    )}
                  </select>
                  <AttachButton disabled={uploadingSub === sub.id}
                    label={uploadingSub === sub.id ? '…' : `📎${(sub.attachments || []).length ? ` ${sub.attachments.length}` : ''}`}
                    title="Attach files to this subtask"
                    onPick={files => attachToSubtask(sub, files)} />
                  {isOwner && (
                    <button type="button" onClick={() => deleteSubtask(sub)} aria-label={`Delete ${sub.title}`}
                      style={{ background:'none', border:'none', color:T.dim, cursor:'pointer', fontSize:12 }}>✕</button>
                  )}
                </div>
                <AttachmentStrip items={sub.attachments}
                  onRemove={(isOwner || sub.createdBy === userId || sub.assigneeId === userId)
                    ? i => removeSubAttachment(sub, i) : undefined} />
              </div>
            );
          })}
          <form onSubmit={addSubtask} style={{ marginTop:8 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <input value={newSub} onChange={e => setNewSub(e.target.value)}
                placeholder="Add subtask…" aria-label="New subtask"
                style={{ flex:'1 1 180px', background:T.panel2, border:`1px solid ${T.border}`,
                  color:T.text, padding:'7px 10px', borderRadius:6, fontFamily:T.sans, fontSize:12, outline:'none' }} />
              <select value={newSubAssignee} onChange={e => setNewSubAssignee(e.target.value)}
                aria-label="Assign new subtask to"
                style={{ flex:'0 1 160px', background:T.panel2, border:`1px solid ${T.border}`,
                  color: newSubAssignee ? T.text : T.dim, padding:'7px 8px', borderRadius:6,
                  fontFamily:T.sans, fontSize:12, outline:'none' }}>
                <option value="">Unassigned</option>
                <option value="me">Me</option>
                {taskPeople.length > 0 && (
                  <optgroup label="On this task">
                    {taskPeople.map(u => <option key={u} value={u}>{nameOf(u)}</option>)}
                  </optgroup>
                )}
                {otherPeople.length > 0 && (
                  <optgroup label="Add to this task">
                    {otherPeople.map(m => <option key={m.uid} value={m.uid}>{m.name || m.email}</option>)}
                  </optgroup>
                )}
              </select>
              <AttachButton onPick={files => setNewSubFiles(p => [...p, ...files])} disabled={savingSub} />
              <button type="submit" disabled={savingSub || !newSub.trim()}
                style={{ padding:'7px 14px', fontFamily:T.mono, fontSize:9,
                  background:`${T.accent}18`, border:`1px solid ${T.accent}`,
                  color:T.accent, cursor: savingSub ? 'wait' : 'pointer', borderRadius:6 }}>
                {savingSub ? '…' : 'ADD'}
              </button>
            </div>
            <PendingFiles files={newSubFiles} onRemove={i => setNewSubFiles(p => p.filter((_, j) => j !== i))} />
            {newSubAssignee && newSubAssignee !== 'me' && !inTask(newSubAssignee) && (
              <div style={{ fontFamily:T.mono, fontSize:9, color:T.yellow, marginTop:6 }}>
                {nameOf(newSubAssignee)} will be added to this task{isTeam ? '' : ' (it becomes a team task)'} so they can see it.
              </div>
            )}
          </form>
        </div>

        {/* Start date */}
        <div style={{ padding:'10px 20px', borderBottom:`1px solid ${T.border}` }}>
          <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, letterSpacing:2, marginBottom:4 }}>STARTED</div>
          <div style={{ fontFamily:T.mono, fontSize:10, color:T.muted }}>{task.startDate ? fmtDate(task.startDate) : '—'}</div>
        </div>

        {/* Completion thread (when done today) */}
        {done && (
          <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontFamily:T.mono, fontSize:8, color:T.green, letterSpacing:2, marginBottom:10 }}>
              ✅ COMPLETION LOG
            </div>
            {(todayCompletion?.thread || []).length > 0 && (
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
                {(todayCompletion.thread || []).map(msg => (
                  <div key={msg.id} style={{ padding:'8px 12px', borderRadius:8,
                    background:`${T.green}08`, border:`1px solid ${T.green}22` }}>
                    <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, marginBottom:4 }}>
                      {msg.authorId === userId ? 'You' : msg.authorName} · {new Date(msg.time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}
                    </div>
                    {msg.text && <div style={{ fontSize:13, color:T.text, lineHeight:1.5 }}>{msg.text}</div>}
                    {(msg.attachments || []).length > 0 && (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
                        {msg.attachments.map((a, i) => (
                          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer">
                            {a.type === 'image' ? (
                              <img src={a.url} alt="" style={{ width:64, height:48, objectFit:'cover', borderRadius:4 }} />
                            ) : (
                              <span style={{ fontFamily:T.mono, fontSize:8, color:T.muted }}>📎 {a.name || 'File'}</span>
                            )}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={addCompletionThread}>
              <textarea value={completionText} onChange={e => setCompletionText(e.target.value)}
                placeholder="Add completion notes or @mention teammates…"
                rows={2}
                style={{ width:'100%', boxSizing:'border-box', background:T.panel2,
                  border:`1px solid ${T.border}`, color:T.text, padding:'8px 10px',
                  borderRadius:6, fontFamily:T.sans, fontSize:13, outline:'none', resize:'vertical',
                  marginBottom:8 }} />
              {completionFiles.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                  {completionFiles.map((f, i) => (
                    <span key={i} style={{ fontFamily:T.mono, fontSize:8, color:T.muted }}>📎 {f.name}</span>
                  ))}
                </div>
              )}
              <input ref={completionFileRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx" multiple hidden
                onChange={e => { setCompletionFiles(p => [...p, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
              <div style={{ display:'flex', gap:8 }}>
                <button type="button" onClick={() => completionFileRef.current?.click()}
                  style={{ padding:'6px 12px', fontFamily:T.mono, fontSize:9,
                    background:'transparent', border:`1px solid ${T.border}`, color:T.dim,
                    cursor:'pointer', borderRadius:6 }}>📎 Attach</button>
                <button type="submit" disabled={savingCompletion || (!completionText.trim() && completionFiles.length === 0)}
                  style={{ padding:'6px 14px', fontFamily:T.mono, fontSize:9,
                    background:`${T.green}18`, border:`1px solid ${T.green}`, color:T.green,
                    cursor:'pointer', borderRadius:6 }}>
                  {savingCompletion ? '…' : 'ADD NOTE'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Comments */}
        <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
          <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, letterSpacing:2, marginBottom:10 }}>
            COMMENTS ({(task.comments || []).length})
          </div>
          {allComments.length === 0 && (
            <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, marginBottom:10, textAlign:'center' }}>
              No comments yet.
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
            {topComments.map(c => {
              const renderComment = (cm, isReply) => (
                <div key={cm.id} style={{ padding:'8px 12px', borderRadius:8,
                  marginLeft: isReply ? 18 : 0,
                  background: cm.authorId === userId ? `${T.accent}10` : T.panel2,
                  border:`1px solid ${cm.authorId === userId ? T.accent + '33' : T.border}`,
                  borderLeft: isReply ? `3px solid ${T.accent}55` : undefined }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{ fontFamily:T.mono, fontSize:9, fontWeight:700,
                      color: cm.authorId === userId ? T.accent : T.muted }}>
                      {cm.authorId === userId ? 'You' : cm.authorName}
                    </span>
                    <span style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>
                      {new Date(cm.time).toLocaleString('en-IN', { hour:'2-digit', minute:'2-digit', day:'numeric', month:'short' })}
                    </span>
                    <button type="button" onClick={() => startReply(cm)}
                      style={{ marginLeft:'auto', background:'none', border:'none', color:T.accent,
                        cursor:'pointer', fontFamily:T.mono, fontSize:9 }}>↩ Reply</button>
                    {(cm.authorId === userId || isOwner) && (
                      <button type="button" onClick={() => deleteComment(cm)} aria-label="Delete comment"
                        title={isReply ? 'Delete reply' : 'Delete comment and its replies'}
                        style={{ background:'none', border:'none',
                          color:T.dim, cursor:'pointer', fontSize:11, opacity:0.6 }}>✕</button>
                    )}
                  </div>
                  {cm.text && <div style={{ fontSize:13, color:T.text, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{cm.text}</div>}
                  <AttachmentStrip items={cm.attachments} />
                </div>
              );
              return (
                <div key={c.id} style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {renderComment(c, false)}
                  {repliesOf(c.id).map(r => renderComment(r, true))}
                </div>
              );
            })}
          </div>
          {replyTo && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontFamily:T.mono,
              fontSize:9, color:T.accent }}>
              ↩ Replying to {replyTo.authorId === userId ? 'yourself' : replyTo.authorName}
              <span style={{ color:T.dim, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:220 }}>
                {replyTo.text ? `“${replyTo.text.slice(0, 60)}”` : ''}
              </span>
              <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply"
                style={{ marginLeft:'auto', background:'none', border:'none', color:T.dim, cursor:'pointer', fontSize:11 }}>✕</button>
            </div>
          )}
          <form onSubmit={addComment}>
            <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
              <textarea ref={commentRef} value={newComment} onChange={e => setNewComment(e.target.value)}
                placeholder={replyTo ? 'Write a reply…' : 'Add a comment…'}
                aria-label={replyTo ? 'Reply' : 'Comment'}
                rows={2}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addComment(e); }}
                style={{ flex:1, background:T.panel2, border:`1px solid ${T.border}`,
                  color:T.text, padding:'7px 10px', borderRadius:6,
                  fontFamily:T.sans, fontSize:13, outline:'none', resize:'vertical' }} />
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <AttachButton onPick={files => setCmtFiles(p => [...p, ...files])} disabled={savingCmt} />
                <button type="submit" disabled={savingCmt || (!newComment.trim() && cmtFiles.length === 0)}
                  style={{ padding:'7px 14px', fontFamily:T.mono, fontSize:9,
                    background:`${T.accent}18`, border:`1px solid ${T.accent}`,
                    color:T.accent, cursor: savingCmt ? 'wait' : 'pointer', borderRadius:6, whiteSpace:'nowrap' }}>
                  {savingCmt ? '…' : replyTo ? 'REPLY' : 'POST'}
                </button>
              </div>
            </div>
            <PendingFiles files={cmtFiles} onRemove={i => setCmtFiles(p => p.filter((_, j) => j !== i))} />
          </form>
          <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim, marginTop:4 }}>⌘↵ to submit</div>
        </div>

        {/* Actions */}
        {isOwner && (
          <div style={{ padding:'14px 20px', display:'flex', gap:10 }}>
            <button type="button" onClick={() => { onToggle(task); onClose(); }}
              style={{ flex:1, padding:'9px 0', fontFamily:T.mono, fontSize:10, letterSpacing:1,
                background: done ? `${T.dim}18` : `${T.green}18`,
                border:`1px solid ${done ? T.border : T.green}`,
                color: done ? T.dim : T.green, cursor:'pointer', borderRadius:6 }}>
              {done ? '↩ MARK UNDONE' : '✓ DONE TODAY'}
            </button>
            <button type="button" onClick={() => { onDelete(task); onClose(); }}
              style={{ padding:'9px 16px', fontFamily:T.mono, fontSize:10, letterSpacing:1,
                background:`${T.red}12`, border:`1px solid ${T.red}44`,
                color:T.red, cursor:'pointer', borderRadius:6 }}>
              🗑
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Single Task Card (clickable) ──────────────────────────────────────────────
function TaskRow({ task, userId, userName, members, onToggle, onDelete, isOwner }) {
  const T = useDT();
  const done    = isDoneToday(task, userId);
  const missed  = getMissedCount(task, userId);
  const isTeam  = task.type === 'team';
  const [hover, setHover] = useState(false);
  const [open,  setOpen]  = useState(false);
  const subtasks = task.subtasks || [];
  const subDone  = subtasks.filter(s => s.completed).length;
  const subMine  = subtasks.filter(s => s.assigneeId === userId && !s.completed).length;

  return (
    <>
      <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        onClick={() => setOpen(true)}
        style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'11px 14px',
          background: done ? `${T.green}10` : hover ? T.panel2 : T.panel,
          borderRadius:8, transition:'background 0.15s, box-shadow 0.15s',
          boxShadow: hover ? T.hoverShadow : 'none',
          border:`1px solid ${hover ? (done ? T.green + '44' : T.border) : T.border}`,
          borderLeft:`3px solid ${done ? T.green : missed > 0 ? T.yellow : isTeam ? T.accent : T.purple}`,
          marginBottom:6, cursor:'pointer' }}>

        {/* Checkbox */}
        <button type="button" onClick={e => { e.stopPropagation(); onToggle(task); }}
          style={{ width:20, height:20, borderRadius:4, flexShrink:0, marginTop:1,
            background: done ? T.green : 'transparent',
            border:`2px solid ${done ? T.green : T.border}`,
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
            transition:'all 0.15s' }}>
          {done && <span style={{ color:'#fff', fontSize:11, lineHeight:1 }}>✓</span>}
        </button>

        {/* Title + badges */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
            <span style={{ fontSize:14, color: done ? T.green : T.bright, fontWeight: 600 }}>
              {task.title}
            </span>
            {isTeam
              ? <span style={{ fontFamily:T.mono, fontSize:8, color:T.accent,
                  background:`${T.accent}15`, border:`1px solid ${T.accent}33`,
                  padding:'1px 7px', borderRadius:8 }}>TEAM</span>
              : <span style={{ fontFamily:T.mono, fontSize:8, color:T.purple,
                  background:`${T.purple}15`, border:`1px solid ${T.purple}33`,
                  padding:'1px 7px', borderRadius:8 }}>🔒 PRIVATE</span>
            }
            {missed > 0 && !done && (
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.yellow,
                background:`${T.yellow}15`, border:`1px solid ${T.yellow}33`,
                padding:'1px 7px', borderRadius:8 }}>
                📌 {missed}d overdue
              </span>
            )}
            {subtasks.length > 0 && (
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>
                {subDone}/{subtasks.length} subtasks
              </span>
            )}
            {subMine > 0 && (
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.accent,
                background:`${T.accent}15`, border:`1px solid ${T.accent}33`,
                padding:'1px 7px', borderRadius:8 }}>
                📌 {subMine} for you
              </span>
            )}
            {task.dueDate && !done && (
              <span style={{ fontFamily:T.mono, fontSize:8,
                color: task.dueDate < todayStr() ? T.red : task.dueDate === todayStr() ? T.yellow : T.dim }}>
                📅 {task.dueDate < todayStr() ? 'OVERDUE' : task.dueDate === todayStr() ? 'DUE TODAY' : `Due ${fmtDate(task.dueDate)}`}
              </span>
            )}
            {(task.comments || []).length > 0 && (
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>
                💬 {task.comments.length}
              </span>
            )}
          </div>

          {/* Team member completion dots */}
          {isTeam && (
            <div style={{ display:'flex', gap:4, alignItems:'center', flexWrap:'wrap' }}>
              {(task.teamMembers || []).map(uid => {
                const name = task.memberNames?.[uid] || uid.slice(0, 8);
                const memberDone = isDoneToday(task, uid);
                return (
                  <div key={uid} title={`${name}: ${memberDone ? 'done' : 'pending'}`}
                    style={{ width:14, height:14, borderRadius:'50%',
                      background: memberDone ? `${T.green}30` : `${T.border}44`,
                      border:`1px solid ${memberDone ? T.green : T.border}`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:7,
                      fontFamily:T.mono, color: memberDone ? T.green : T.dim }}>
                    {memberDone ? '✓' : (name[0]||'?').toUpperCase()}
                  </div>
                );
              })}
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.dim, marginLeft:2 }}>
                {(task.teamMembers||[]).filter(uid => isDoneToday(task, uid)).length}/{(task.teamMembers||[]).length}
              </span>
            </div>
          )}
        </div>

        {/* Delete on hover (owner only) */}
        {isOwner && hover && (
          <button type="button" onClick={e => { e.stopPropagation(); onDelete(task); }}
            style={{ background:'none', border:'none', color:T.dim, cursor:'pointer',
              fontSize:14, padding:'0 2px', flexShrink:0, opacity:0.6 }}>✕</button>
        )}
      </div>

      {open && (
        <TaskDetailModal
          task={task} userId={userId} userName={userName} members={members}
          onToggle={onToggle} onDelete={onDelete} isOwner={isOwner}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Completed Tab (past completions + thread) ───────────────────────────────
function CompletedThreadModal({ entry, userId, userName, members, onClose }) {
  const T = useDT();
  const { task, date, log } = entry;
  const [thread, setThread] = useState(log.thread || []);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setThread(log.thread || []);
  }, [log.thread]);

  async function handleSubmit(e) {
    e.preventDefault();
    if ((!text.trim() && files.length === 0) || saving) return;
    setSaving(true);
    try {
      let attachments = [];
      for (const file of files) {
        attachments.push(await uploadTaskThreadFile(task.id, date, file));
      }
      const message = {
        id: Date.now().toString(),
        text: text.trim(),
        authorId: userId,
        authorName: userName || 'Me',
        time: new Date().toISOString(),
        attachments,
      };
      const logs = { ...(task.completionLogs || {}) };
      const userLogs = { ...(logs[userId] || {}) };
      const existing = userLogs[date] || { completedAt: log.completedAt, thread: [] };
      userLogs[date] = { ...existing, thread: [...(existing.thread || []), message] };
      logs[userId] = userLogs;
      await updateDoc(doc(db(), 'daily_tasks', task.id), { completionLogs: logs });
      setThread(userLogs[date].thread);

      const mentioned = extractMentions(text, members || []);
      for (const m of mentioned) {
        await createNotification({
          recipientId: m.uid,
          type: 'mention',
          title: `🚀 ${userName || 'Someone'} mentioned you`,
          body: `On completed task "${task.title}": ${text.trim().slice(0, 80)}`,
          fromId: userId,
          fromName: userName || 'Me',
          link: 'tasks',
        });
      }
      setText('');
      setFiles([]);
    } catch (ex) {
      console.warn('Completed thread post failed:', ex);
    }
    setSaving(false);
  }

  const isTeam = task.type === 'team';

  return (
    <div style={{ position:'fixed', inset:0, background:T.overlay, zIndex:500,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'20px 16px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:14,
        width:'100%', maxWidth:560, maxHeight:'85vh', overflowY:'auto',
        borderLeft:`4px solid ${T.green}` }}>
        <div style={{ padding:'18px 20px', borderBottom:`1px solid ${T.border}`,
          display:'flex', alignItems:'flex-start', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:16, fontWeight:700, color:T.bright, marginBottom:6 }}>{task.title}</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.green,
                background:`${T.green}15`, border:`1px solid ${T.green}33`,
                padding:'1px 7px', borderRadius:8 }}>✅ COMPLETED</span>
              <span style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>
                {fmtDateLong(date)}
              </span>
              {isTeam && (
                <span style={{ fontFamily:T.mono, fontSize:8, color:T.accent,
                  background:`${T.accent}15`, border:`1px solid ${T.accent}33`,
                  padding:'1px 7px', borderRadius:8 }}>TEAM</span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background:'none', border:'none', color:T.dim, cursor:'pointer', fontSize:20 }}>×</button>
        </div>

        {task.description && (
          <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
            fontSize:13, color:T.muted, lineHeight:1.55 }}>
            {task.description}
          </div>
        )}

        <div style={{ padding:'16px 20px' }}>
          <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, letterSpacing:2, marginBottom:12 }}>
            COMPLETION THREAD
          </div>
          {thread.length === 0 && (
            <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, marginBottom:12 }}>
              No notes yet. Add a summary or attachments below.
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
            {thread.map(msg => (
              <div key={msg.id} style={{ padding:'10px 12px', borderRadius:8,
                background: msg.authorId === userId ? `${T.accent}10` : T.panel2,
                border:`1px solid ${msg.authorId === userId ? T.accent + '33' : T.border}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontFamily:T.mono, fontSize:9, fontWeight:700,
                    color: msg.authorId === userId ? T.accent : T.muted }}>
                    {msg.authorId === userId ? 'You' : msg.authorName}
                  </span>
                  <span style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>
                    {new Date(msg.time).toLocaleString('en-IN', { hour:'2-digit', minute:'2-digit', day:'numeric', month:'short' })}
                  </span>
                </div>
                {msg.text && (
                  <div style={{ fontSize:13, color:T.text, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{msg.text}</div>
                )}
                {(msg.attachments || []).length > 0 && (
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
                    {msg.attachments.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                        style={{ display:'block', textDecoration:'none' }}>
                        {a.type === 'image' ? (
                          <img src={a.url} alt={a.name || ''} style={{ width:72, height:54, objectFit:'cover', borderRadius:6,
                            border:`1px solid ${T.border}` }} />
                        ) : (
                          <div style={{ padding:'6px 10px', borderRadius:6, background:T.panel2,
                            border:`1px solid ${T.border}`, fontFamily:T.mono, fontSize:8, color:T.muted }}>
                            📎 {a.name || 'Attachment'}
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder="Add notes… @Name to mention teammates"
              rows={3}
              style={{ width:'100%', boxSizing:'border-box', background:T.panel2,
                border:`1px solid ${T.border}`, color:T.text, padding:'10px 12px',
                borderRadius:8, fontFamily:T.sans, fontSize:13, outline:'none', resize:'vertical',
                marginBottom:8 }} />
            {files.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                {files.map((f, i) => (
                  <span key={i} style={{ fontFamily:T.mono, fontSize:8, color:T.muted,
                    background:T.panel2, border:`1px solid ${T.border}`, padding:'3px 8px', borderRadius:6 }}>
                    📎 {f.name}
                    <button type="button" onClick={() => setFiles(p => p.filter((_, j) => j !== i))}
                      style={{ marginLeft:6, background:'none', border:'none', color:T.dim, cursor:'pointer' }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx" multiple hidden
              onChange={e => { setFiles(p => [...p, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <button type="button" onClick={() => fileRef.current?.click()}
                style={{ padding:'7px 12px', fontFamily:T.mono, fontSize:9,
                  background:'transparent', border:`1px solid ${T.border}`, color:T.dim,
                  cursor:'pointer', borderRadius:6 }}>
                📎 Attach
              </button>
              <div style={{ flex:1 }} />
              <button type="submit" disabled={saving || (!text.trim() && files.length === 0)}
                style={{ padding:'7px 16px', fontFamily:T.mono, fontSize:9,
                  background:`${T.green}18`, border:`1px solid ${T.green}`,
                  color:T.green, cursor:'pointer', borderRadius:6 }}>
                {saving ? 'SAVING…' : 'ADD TO THREAD'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CompletedTab({ tasks, userId, userName, members }) {
  const T = useDT();
  const entries = getCompletedEntries(tasks, userId);
  const [openEntry, setOpenEntry] = useState(null);

  if (entries.length === 0) {
    return (
      <div style={{ textAlign:'center', padding:'48px 20px' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>✅</div>
        <div style={{ fontFamily:T.mono, fontSize:11, color:T.dim, marginBottom:6 }}>
          No completed tasks yet
        </div>
        <div style={{ fontFamily:T.sans, fontSize:12, color:`${T.dim}99`, lineHeight:1.5 }}>
          When you mark a task done on the Today tab, it appears here<br />
          with your completion notes and attachments.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, letterSpacing:2, marginBottom:16 }}>
        COMPLETED TASKS ({entries.length})
      </div>
      {entries.map(({ task, date, log }) => {
        const threadCount = (log.thread || []).length;
        const attachCount = (log.thread || []).reduce((n, m) => n + (m.attachments?.length || 0), 0);
        const isTeam = task.type === 'team';
        return (
          <div key={`${task.id}-${date}`} onClick={() => setOpenEntry({ task, date, log })}
            style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 14px',
              background:T.panel, borderRadius:8, marginBottom:8, cursor:'pointer',
              border:`1px solid ${T.border}`, borderLeft:`3px solid ${T.green}`,
              transition:'background 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = T.panel2; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.panel; }}
          >
            <div style={{ width:22, height:22, borderRadius:5, flexShrink:0, marginTop:1,
              background:T.green, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontSize:12 }}>✓</span>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                <span style={{ fontSize:14, fontWeight:600, color:T.bright }}>{task.title}</span>
                {isTeam ? (
                  <span style={{ fontFamily:T.mono, fontSize:8, color:T.accent,
                    background:`${T.accent}15`, border:`1px solid ${T.accent}33`,
                    padding:'1px 7px', borderRadius:8 }}>TEAM</span>
                ) : (
                  <span style={{ fontFamily:T.mono, fontSize:8, color:T.purple,
                    background:`${T.purple}15`, border:`1px solid ${T.purple}33`,
                    padding:'1px 7px', borderRadius:8 }}>🔒 PRIVATE</span>
                )}
              </div>
              <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim }}>
                Completed {date === todayStr() ? 'today' : fmtDate(date)}
                {threadCount > 0 && <span style={{ marginLeft:8 }}>💬 {threadCount}</span>}
                {attachCount > 0 && <span style={{ marginLeft:8 }}>📎 {attachCount}</span>}
              </div>
            </div>
            <span style={{ fontFamily:T.mono, fontSize:9, color:T.green, flexShrink:0 }}>OPEN →</span>
          </div>
        );
      })}
      {openEntry && (
        <CompletedThreadModal
          entry={openEntry}
          userId={userId}
          userName={userName}
          members={members}
          onClose={() => setOpenEntry(null)}
        />
      )}
    </div>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────────
function HistoryTab({ tasks, userId }) {
  const T = useDT();
  const days = Array.from({ length: 30 }, (_, i) => offsetStr(-i));

  return (
    <div>
      <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, letterSpacing:2, marginBottom:16 }}>
        COMPLETION HISTORY — LAST 30 DAYS
      </div>
      {days.map(date => {
        const privateTasks = tasks.filter(t => t.type === 'private');
        const teamTasks    = tasks.filter(t => t.type === 'team' && (t.teamMembers||[]).includes(userId));
        const allTasks = [...privateTasks, ...teamTasks];

        // Only show days where the task existed (startDate <= date)
        const relevant = allTasks.filter(t => t.startDate && t.startDate <= date);
        if (relevant.length === 0) return null;

        const done   = relevant.filter(t => (t.completions?.[userId] || []).includes(date));
        const missed = relevant.filter(t => !(t.completions?.[userId] || []).includes(date));
        const rate   = Math.round((done.length / relevant.length) * 100);

        const isToday = date === todayStr();

        return (
          <div key={date} style={{ marginBottom:6, padding:'10px 14px',
            background: isToday ? `${T.accent}08` : T.panel2,
            border:`1px solid ${isToday ? T.accent + '33' : T.border}`,
            borderRadius:8, display:'flex', alignItems:'flex-start', gap:16 }}>
            {/* Date */}
            <div style={{ minWidth:80, flexShrink:0 }}>
              <div style={{ fontFamily:T.mono, fontSize:10, color: isToday ? T.accent : T.bright, fontWeight:700 }}>
                {isToday ? 'TODAY' : fmtDate(date)}
              </div>
              <div style={{ fontFamily:T.mono, fontSize:8, color: rate === 100 ? T.green : rate >= 50 ? T.yellow : T.red, marginTop:2 }}>
                {rate}% done
              </div>
            </div>

            {/* Completion bar */}
            <div style={{ flex:1 }}>
              <div style={{ height:4, background:`${T.border}`, borderRadius:2, marginBottom:8, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${rate}%`,
                  background: rate === 100 ? T.green : rate >= 50 ? T.yellow : T.red,
                  borderRadius:2, transition:'width 0.3s' }} />
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {done.map(t => (
                  <span key={t.id} style={{ fontFamily:T.mono, fontSize:8, color:T.green,
                    background:`${T.green}12`, border:`1px solid ${T.green}33`,
                    padding:'1px 7px', borderRadius:6 }}>✓ {t.title}</span>
                ))}
                {missed.map(t => (
                  <span key={t.id} style={{ fontFamily:T.mono, fontSize:8, color:T.dim,
                    background:`${T.border}22`, border:`1px solid ${T.border}`,
                    padding:'1px 7px', borderRadius:6,
                    textDecoration: isToday ? 'none' : 'line-through' }}>
                    {t.title}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );
      }).filter(Boolean)}
    </div>
  );
}

// ── Assigned Tasks Tab ────────────────────────────────────────────────────────
function AssignedTab({ userId, userName }) {
  const T = useDT();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingTask, setCompletingTask] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const byId = new Map();
    function merge(snap) {
      snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
      setTasks([...byId.values()].sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      }));
      setLoading(false);
    }
    const q1 = query(collection(db(), 'tasks'), where('assigneeId', '==', userId), orderBy('createdAt', 'desc'));
    const q2 = query(collection(db(), 'tasks'), where('assigneeIds', 'array-contains', userId), orderBy('createdAt', 'desc'));
    const u1 = onSnapshot(q1, merge, () => setLoading(false));
    const u2 = onSnapshot(q2, merge, () => setLoading(false));
    return () => { u1(); u2(); };
  }, [userId]);

  async function updateStatus(id, status) {
    if (status === 'completed') {
      const task = tasks.find(t => t.id === id);
      if (task && task.requiresProof !== false) { setCompletingTask(task); return; }
    }
    await updateDoc(doc(db(), 'tasks', id), { ...actorStamp(), status, updatedAt: serverTimestamp() });
  }

  const STATUSES = [
    { key:'pending',     label:'PENDING',     color:T.yellow },
    { key:'in_progress', label:'IN PROGRESS', color:'#0ea5e9' },
    { key:'completed',   label:'DONE',        color:T.green  },
  ];

  const PRIORITY_COLOR = { low:'#94a3b8', medium:T.yellow, high:'#f97316', critical:T.red };

  if (loading) return <div style={{ padding:60, textAlign:'center', fontFamily:T.mono, fontSize:10, color:T.dim }}>LOADING…</div>;
  if (!tasks.length) return (
    <div style={{ padding:'60px 0', textAlign:'center', fontFamily:T.mono, fontSize:11, color:T.dim }}>
      No tasks assigned to you yet.
    </div>
  );

  const active    = tasks.filter(t => t.status !== 'completed');
  const completed = tasks.filter(t => t.status === 'completed');

  return (
    <div>
      <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim, letterSpacing:2, marginBottom:12 }}>
        TASKS ASSIGNED TO YOU ({active.length} active)
      </div>
      {[...active, ...completed].map(task => {
        const st = STATUSES.find(s => s.key === task.status) || STATUSES[0];
        const pc = PRIORITY_COLOR[task.priority] || T.dim;
        const isDone = task.status === 'completed';
        return (
          <div key={task.id} style={{ padding:'14px 16px', marginBottom:8,
            background: isDone ? `${T.green}06` : T.panel2,
            border:`1px solid ${isDone ? T.green + '22' : T.border}`,
            borderRadius:8, borderLeft:`3px solid ${st.color}` }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:0 }}>
                {task.sourceType === 'mission_log' && (
                  <div style={{ fontFamily:T.mono, fontSize:8, letterSpacing:'0.1em', color:'#0ea5e9', marginBottom:4 }}>
                    🚀 FROM MISSION LOG
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:600, fontSize:14, color: isDone ? T.dim : T.bright,
                    textDecoration: isDone ? 'line-through' : 'none' }}>
                    {task.title}
                  </span>
                  <span style={{ fontFamily:T.mono, fontSize:8, color:pc,
                    background:`${pc}15`, border:`1px solid ${pc}33`,
                    padding:'1px 7px', borderRadius:6 }}>
                    {(task.priority||'medium').toUpperCase()}
                  </span>
                  {task.dueDate && (
                    <span style={{ fontFamily:T.mono, fontSize:8, color:T.muted }}>
                      Due: {task.dueDate}
                    </span>
                  )}
                </div>
                {task.description && (
                  <div style={{ fontSize:12, color:T.muted, marginBottom:8, lineHeight:1.5 }}>
                    {task.description}
                  </div>
                )}
                {task.sourceType === 'mission_log' && task.sourceLink && (
                  <a href={task.sourceLink}
                    style={{ display:'inline-block', marginBottom:8, fontFamily:T.mono, fontSize:9,
                      color:'#0ea5e9', textDecoration:'none', letterSpacing:'0.06em' }}>
                    View Mission Log →
                  </a>
                )}
                <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>
                  Assigned by {task.assigneeName !== task.createdBy ? task.createdBy : 'team'}
                </div>
              </div>
              {/* Status controls */}
              <div style={{ display:'flex', gap:5, flexShrink:0, flexWrap:'wrap' }}>
                {STATUSES.map(s => (
                  <button key={s.key} onClick={() => updateStatus(task.id, s.key)}
                    style={{ padding:'4px 10px', fontFamily:T.mono, fontSize:8, cursor:'pointer',
                      background: task.status === s.key ? `${s.color}20` : 'transparent',
                      border:`1px solid ${task.status === s.key ? s.color : T.border}`,
                      color: task.status === s.key ? s.color : T.dim, borderRadius:4 }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}
      {completingTask && (
        <TaskCompleteModal task={completingTask} userId={userId} userName={userName}
          onClose={() => setCompletingTask(null)} onCompleted={() => setCompletingTask(null)} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function DailyTasksPage({ user, embedded = false, onOpenFull }) {
  const appTheme = useTheme();
  const colors = useMemo(() => buildDailyTasksColors(appTheme), [appTheme]);

  return (
    <DailyTasksThemeContext.Provider value={colors}>
      <DailyTasksPageInner user={user} embedded={embedded} onOpenFull={onOpenFull} />
    </DailyTasksThemeContext.Provider>
  );
}

function DailyTasksPageInner({ user, embedded = false, onOpenFull }) {
  const T = useDT();
  const uid      = user.uid;
  const uName    = user.name || 'Me';

  const [tab,            setTab]            = useState('today');
  const [tasks,          setTasks]          = useState([]);
  const [members,        setMembers]        = useState([]);
  const [showAddPrivate, setShowAddPrivate] = useState(false);
  const [showAddTeam,    setShowAddTeam]    = useState(false);
  const [errMsg,         setErrMsg]         = useState('');

  const TODAY = todayStr();

  // ── Load daily tasks: owned by me OR I'm a team member ─────────────────────
  useEffect(() => {
    const bucket = {};
    const merge = () => setTasks(Object.values(bucket).sort((a, b) => {
      // Sort: pending first, then by creation date
      const aDone = isDoneToday(a, uid) ? 1 : 0;
      const bDone = isDoneToday(b, uid) ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      const ta = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const tb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return ta - tb;
    }));

    // Simple single-field queries (no composite index required)
    const q1 = query(collection(db(), 'daily_tasks'), where('ownerId', '==', uid));
    const q2 = query(collection(db(), 'daily_tasks'), where('teamMembers', 'array-contains', uid));

    const onQueryError = ex => {
      setErrMsg(`Couldn't load tasks — ${ex.code === 'permission-denied' ? 'no permission' : (ex.message || 'try again')}`);
      setTimeout(() => setErrMsg(''), 4000);
    };
    const unsub1 = onSnapshot(q1, snap => {
      snap.docs.forEach(d => {
        const data = { id:d.id, ...d.data() };
        if (data.active !== false) bucket[d.id] = data; // client-side active filter
        else delete bucket[d.id];
      });
      merge();
    }, onQueryError);
    const unsub2 = onSnapshot(q2, snap => {
      snap.docs.forEach(d => {
        const data = { id:d.id, ...d.data() };
        if (data.active !== false) bucket[d.id] = data;
        else delete bucket[d.id];
      });
      merge();
    }, onQueryError);

    return () => { unsub1(); unsub2(); };
  }, [uid]);

  // ── Load team members ───────────────────────────────────────────────────────
  useEffect(() => {
    return onSnapshot(collection(db(), 'authorized_users'), snap => {
      setMembers(snap.docs.map(d => ({ uid:d.id, ...d.data() })).filter(m => m.active !== false && m.uid !== uid));
    }, () => {});
  }, [uid]);

  // ── Toggle completion for today ─────────────────────────────────────────────
  async function toggleComplete(task) {
    const done = isDoneToday(task, uid);
    const logs = { ...(task.completionLogs || {}) };
    const userLogs = { ...(logs[uid] || {}) };

    try {
      if (done) {
        delete userLogs[TODAY];
        logs[uid] = userLogs;
        await updateDoc(doc(db(), 'daily_tasks', task.id), {
          [`completions.${uid}`]: arrayRemove(TODAY),
          completionLogs: logs,
        });
      } else {
        userLogs[TODAY] = {
          completedAt: new Date().toISOString(),
          thread: userLogs[TODAY]?.thread || [],
        };
        logs[uid] = userLogs;
        await updateDoc(doc(db(), 'daily_tasks', task.id), {
          [`completions.${uid}`]: arrayUnion(TODAY),
          completionLogs: logs,
        });
      }
    } catch (ex) {
      setErrMsg(`Couldn't save — ${ex.code === 'permission-denied' ? 'no permission to update this task' : (ex.message || 'try again')}`);
      setTimeout(() => setErrMsg(''), 4000);
    }
  }

  // ── Delete task (mark inactive) ─────────────────────────────────────────────
  async function deleteTask(task) {
    if (!window.confirm(`Remove "${task.title}" from your daily tasks?`)) return;
    await updateDoc(doc(db(), 'daily_tasks', task.id), { active: false });
  }

  // ── Derived lists ───────────────────────────────────────────────────────────
  const privateTasks = tasks.filter(t => t.type === 'private' && t.ownerId === uid);
  const teamTasks    = tasks.filter(t => t.type === 'team');

  const privateToday = privateTasks.filter(t => isScheduledForToday(t, TODAY));
  const teamToday    = teamTasks.filter(t => isScheduledForToday(t, TODAY));

  const privatePending = privateToday.filter(t => !isDoneToday(t, uid));
  const privateDone    = privateToday.filter(t =>  isDoneToday(t, uid));
  const teamPending    = teamToday.filter(t => !isDoneToday(t, uid));
  const teamDone       = teamToday.filter(t =>  isDoneToday(t, uid));

  const totalToday     = privateToday.length + teamToday.length;
  const completedToday = privateDone.length + teamDone.length;
  const pct = totalToday ? Math.round((completedToday / totalToday) * 100) : 0;
  const completedCount = getCompletedEntries(tasks, uid).length;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={embedded ? 'smc-daily-tasks-embedded' : undefined}
      style={{ fontFamily:T.sans, maxWidth:820, margin:'0 auto',
      padding: embedded ? '0 0 8px' : '24px 16px 80px',
      background: embedded ? 'transparent' : T.bg, color: T.text,
      borderRadius: embedded ? 0 : 12 }}>

      {errMsg && (
        <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)',
          background:'#2d0a0a', border:'1px solid #ef4444', color:'#f87171',
          padding:'10px 20px', borderRadius:8, fontFamily:T.mono, fontSize:11,
          letterSpacing:0.5, zIndex:9999, boxShadow:'0 4px 24px #00000055', maxWidth:'90vw' }}>
          {errMsg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: embedded ? 12 : 20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontFamily:T.mono, fontSize:11, color:T.purple, letterSpacing:3, marginBottom:2 }}>
              📅 DAILY TASKS
            </div>
            <div style={{ fontFamily:T.mono, fontSize:9, color:T.dim }}>
              {fmtDateLong(TODAY)}
            </div>
          </div>
          <div style={{ flex:1 }} />
          {embedded && onOpenFull && (
            <button type="button" onClick={onOpenFull}
              style={{ padding:'6px 12px', fontFamily:T.mono, fontSize:9, letterSpacing:1,
                background:`${T.purple}14`, border:`1px solid ${T.purple}44`, color:T.purple,
                borderRadius:6, cursor:'pointer', whiteSpace:'nowrap' }}>
              OPEN FULL TAB →
            </button>
          )}
          {/* Progress ring summary */}
          {totalToday > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:10,
              background:T.panel2, border:`1px solid ${T.border}`, borderRadius:10, padding:'8px 14px' }}>
              <div style={{ position:'relative', width:36, height:36 }}>
                <svg width="36" height="36" style={{ transform:'rotate(-90deg)' }}>
                  <circle cx="18" cy="18" r="14" fill="none" stroke={`${T.border}`} strokeWidth="3" />
                  <circle cx="18" cy="18" r="14" fill="none"
                    stroke={pct === 100 ? T.green : T.purple} strokeWidth="3"
                    strokeDasharray={`${2 * Math.PI * 14}`}
                    strokeDashoffset={`${2 * Math.PI * 14 * (1 - pct / 100)}`}
                    strokeLinecap="round" />
                </svg>
                <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center',
                  justifyContent:'center', fontFamily:T.mono, fontSize:9,
                  color: pct === 100 ? T.green : T.bright }}>
                  {pct}%
                </div>
              </div>
              <div>
                <div style={{ fontFamily:T.mono, fontSize:11, color:T.bright, fontWeight:700 }}>
                  {completedToday}/{totalToday}
                </div>
                <div style={{ fontFamily:T.mono, fontSize:8, color:T.dim }}>today</div>
              </div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {totalToday > 0 && (
          <div style={{ height:4, background:T.border, borderRadius:2, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`,
              background: pct === 100 ? T.green : T.purple,
              borderRadius:2, transition:'width 0.4s' }} />
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="smc-daily-tasks-tabs" style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:24, overflowX:'auto' }}>
        {[
          ['today',     '📅 TODAY'],
          ['completed', '✅ COMPLETED'],
          ['history',   '📊 HISTORY'],
          ['assigned',  '📋 ASSIGNED TO ME'],
        ].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ padding:'10px 18px', fontFamily:T.mono, fontSize:10, letterSpacing:1,
              background:'transparent', border:'none', cursor:'pointer', marginBottom:-1,
              borderBottom:`2px solid ${tab === v ? T.purple : 'transparent'}`,
              color: tab === v ? T.purple : T.muted, whiteSpace:'nowrap', flexShrink:0 }}>
            {l}{v === 'completed' && completedCount > 0 ? ` (${completedCount})` : ''}
          </button>
        ))}
      </div>

      {/* ── TODAY TAB ── */}
      {tab === 'today' && (
        <div>
          {totalToday === 0 && !showAddPrivate && !showAddTeam && (
            <div style={{ textAlign:'center', padding:'40px 0', fontFamily:T.mono, fontSize:11, color:T.dim }}>
              No tasks scheduled for today. Add your first task below.
            </div>
          )}

          {totalToday > 0 && privatePending.length === 0 && teamPending.length === 0 && (
            <div style={{ textAlign:'center', padding:'20px 16px', marginBottom:20,
              background:`${T.green}08`, border:`1px solid ${T.green}33`, borderRadius:10 }}>
              <div style={{ fontFamily:T.mono, fontSize:11, color:T.green, letterSpacing:1 }}>
                All pending tasks done for today
              </div>
              <button type="button" onClick={() => setTab('completed')}
                style={{ marginTop:10, padding:'6px 14px', fontFamily:T.mono, fontSize:9,
                  background:`${T.green}14`, border:`1px solid ${T.green}44`, color:T.green,
                  borderRadius:6, cursor:'pointer' }}>
                VIEW COMPLETED →
              </button>
            </div>
          )}

          {/* ── PRIVATE TASKS ── */}
          <div style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <div style={{ fontFamily:T.mono, fontSize:9, color:T.purple, letterSpacing:2, fontWeight:700 }}>
                🔒 PRIVATE TASKS
              </div>
              <div style={{ flex:1, height:1, background:`${T.purple}22` }} />
              <button onClick={() => { setShowAddPrivate(s => !s); setShowAddTeam(false); }}
                style={{ padding:'4px 12px', fontFamily:T.mono, fontSize:9,
                  background: showAddPrivate ? `${T.purple}20` : 'transparent',
                  border:`1px solid ${showAddPrivate ? T.purple : T.border}`,
                  color: showAddPrivate ? T.purple : T.muted, cursor:'pointer', borderRadius:4 }}>
                + ADD
              </button>
            </div>

            {showAddPrivate && (
              <AddPrivateTaskForm userId={uid} onClose={() => setShowAddPrivate(false)} />
            )}

            {privatePending.length === 0 && privateToday.length === 0 && !showAddPrivate && (
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, padding:'8px 0', textAlign:'center' }}>
                — No private tasks for today. Add one above. —
              </div>
            )}
            {privatePending.length === 0 && privateToday.length > 0 && !showAddPrivate && (
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, padding:'8px 0', textAlign:'center' }}>
                — All private tasks done — see Completed tab. —
              </div>
            )}

            {privatePending.map(task => (
              <TaskRow key={task.id} task={task} userId={uid} userName={uName} members={members}
                onToggle={toggleComplete} onDelete={deleteTask} isOwner={task.ownerId === uid} />
            ))}
          </div>

          {/* ── TEAM TASKS ── */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <div style={{ fontFamily:T.mono, fontSize:9, color:T.accent, letterSpacing:2, fontWeight:700 }}>
                👥 TEAM TASKS
              </div>
              <div style={{ flex:1, height:1, background:`${T.accent}22` }} />
              <button onClick={() => { setShowAddTeam(s => !s); setShowAddPrivate(false); }}
                style={{ padding:'4px 12px', fontFamily:T.mono, fontSize:9,
                  background: showAddTeam ? `${T.accent}20` : 'transparent',
                  border:`1px solid ${showAddTeam ? T.accent : T.border}`,
                  color: showAddTeam ? T.accent : T.muted, cursor:'pointer', borderRadius:4 }}>
                + ADD
              </button>
            </div>

            {showAddTeam && (
              <AddTeamTaskForm userId={uid} userName={uName} members={members}
                onClose={() => setShowAddTeam(false)} />
            )}

            {teamPending.length === 0 && teamToday.length === 0 && !showAddTeam && (
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, padding:'8px 0', textAlign:'center' }}>
                — No team tasks for today. Create one above. —
              </div>
            )}
            {teamPending.length === 0 && teamToday.length > 0 && !showAddTeam && (
              <div style={{ fontFamily:T.mono, fontSize:10, color:T.dim, padding:'8px 0', textAlign:'center' }}>
                — All team tasks done — see Completed tab. —
              </div>
            )}

            {teamPending.map(task => (
              <TaskRow key={task.id} task={task} userId={uid} userName={uName} members={members}
                onToggle={toggleComplete} onDelete={deleteTask} isOwner={task.ownerId === uid} />
            ))}
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <HistoryTab tasks={tasks} userId={uid} />
      )}

      {/* ── COMPLETED TAB ── */}
      {tab === 'completed' && (
        <CompletedTab tasks={tasks} userId={uid} userName={uName} members={members} />
      )}

      {/* ── ASSIGNED TAB ── */}
      {tab === 'assigned' && (
        <AssignedTab userId={uid} userName={uName} />
      )}
    </div>
  );
}
